import { faRotate } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { exhaustive } from "exhaustive"
import { DateTime } from "luxon"
import { FC, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { AbsolutCentered } from "../../../AbsolutCentered/AbsolutCentered"
import { useAuth } from "../../../Auth/AuthContext"
import { useClient } from "../../../Client/ClientAndUserProvider"
import { ArrowLeftIcon, ArrowRightIcon } from "../../../Icons/Icon"
import { Loader } from "../../../Loader/Loader"
import { useNavigator } from "../../../Navigator/useNavigator"
import { API } from "../../../network/API"
import { parseNumberSafe } from "../../../Orders/Helpers"
import {
	rowsPerPageSection,
	SkrappyTable,
	TableColumn,
	tablePagingSection,
} from "../../../Shared/SkrappyTable/SkrappyTable"
import { Paging, setPageAndFilterInUrl } from "../../CustomerPortalOrders/CustomerPortalOrders"
import style from "./ExternalIntegrationEvents.module.css"

type Props = {
	onCancel: () => void
}

type ExtraExternalEventDataBase = {}

type GetCoredinationSyncEventAllConsumers = ExtraExternalEventDataBase & {
	type: "CoredinationAllConsumers"
	expectedAmount: number
}

type GetCoredinationSyncSpecificConsumerInbound = ExtraExternalEventDataBase & {
	type: "CoredinationSpecificConsumerInbound"
	consumerRef: string
	coredinationCustomerUuid: string
}

type GetCoredinationSyncSpecificCustomerOutbound = ExtraExternalEventDataBase & {
	type: "CoredinationSpecificCustomerOutbound"
	consumerRef: string
}

type GetCoredinationSyncEventAllJobs = ExtraExternalEventDataBase & {
	type: "CoredinationAllJobs"
	expectedAmount: number
}

type GetCoredinationSyncSpecificJobInbound = ExtraExternalEventDataBase & {
	type: "CoredinationSpecificJobInbound"
	coredinationJobUuid: string
	coredinationJobNo?: number
}

type ExtraExternalEventData =
	| GetCoredinationSyncEventAllConsumers
	| GetCoredinationSyncSpecificConsumerInbound
	| GetCoredinationSyncSpecificCustomerOutbound
	| GetCoredinationSyncEventAllJobs
	| GetCoredinationSyncSpecificJobInbound

type GetExternalIntegrationEventData = {
	id: string
	timestampMillisecond: number
	data: ExtraExternalEventData
}

type ExternalIntegrationSlimConsumer = {
	id: string
	name: string
}

type GetExternalIntegrationEvent = {
	event: GetExternalIntegrationEventData
	consumer?: ExternalIntegrationSlimConsumer
}

type GetExternalIntegrationEvents = {
	events: GetExternalIntegrationEvent[]
	paging: Paging
}

export const ExternalIntegrationEvents: FC<Props> = (props) => {
	const auth = useAuth()
	const client = useClient()
	const navigator = useNavigator()

	const [queryParams, setQueryParams] = useSearchParams({ page: "1", pageSize: "20" })
	const [events, setEvents] = useState<GetExternalIntegrationEvent[] | null>(null)
	const [paging, setPaging] = useState<Paging | null>(null)

	useEffect(() => {
		fetchEvents()
	}, [queryParams])

	function fetchEvents() {
		setEvents(null)

		const pageNumber = getPageNumber()
		API.getWithRetries<GetExternalIntegrationEvents>(
			`/external-integration-events-v1/${client.identifier}/all?page=${
				pageNumber <= 0 ? 0 : pageNumber - 1
			}&size=${getPageSize()}`,
			true,
			undefined,
			10,
		).then(
			(res) => {
				setEvents(res.events)
				setPaging(res.paging)
			},
			() => {},
		)
	}

	function getPageNumber() {
		return parseNumberSafe(queryParams.get("page"), 1)
	}

	function getPageSize() {
		return parseNumberSafe(queryParams.get("pageSize"), 20)
	}

	function tableColumns(): TableColumn<GetExternalIntegrationEvent>[] {
		return [
			{
				headerContent: "Händelse",
				cellContent: {
					content: (data) => {
						return exhaustive(data.event.data, "type", {
							CoredinationAllJobs: (it) => {
								return <div>Coredination: Synkronisering av alla uppdrag</div>
							},
							CoredinationSpecificJobInbound: (it) => {
								return (
									<div>
										Coredination: Synkronisering av specifikt uppdrag
										{it.coredinationJobNo !== undefined ? `#${it.coredinationJobNo}` : null}
									</div>
								)
							},
							CoredinationAllConsumers: (it) => {
								return <div>Coredination: Synkronisering av alla kunder</div>
							},
							CoredinationSpecificConsumerInbound: (it) => {
								return (
									<div>
										Coredination: Synkronisering av kund från coredination:{" "}
										{data.consumer?.name || it.consumerRef}
									</div>
								)
							},
							CoredinationSpecificCustomerOutbound: (it) => {
								return (
									<div>
										Coredination: Uppdatering av kund mot coredination:{" "}
										{data.consumer?.name || it.consumerRef}
									</div>
								)
							},
							_: () => null,
						})
					},
				},
			},
			{
				headerContent: "Händelsetid",
				cellContent: {
					content: (data) => (
						<div className={style.createdAt}>
							{DateTime.fromMillis(data.event.timestampMillisecond)
								.setLocale("sv-SE")
								.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}
						</div>
					),
				},
			},
			{
				headerContent: "",
				cellContent: {
					content: (data) => {
						const consumer = data.consumer
						if (consumer) {
							return (
								<ArrowRightIcon
									className={style.goToConsumerIconButton}
									size={22}
									onClick={() => {
										navigator.open(`my-pages/consumers?tab=overview&consumerId=${consumer.id}`)
									}}
								/>
							)
						}

						return null
					},
				},
			},
		]
	}

	function table() {
		if (events === null) {
			return (
				<div className={style.tableWrapper}>
					<div style={{ display: "flex", gap: "15px", marginBottom: "10px" }}>
						<div
							className={style.cancelButton}
							onClick={() => {
								props.onCancel()
							}}>
							<ArrowLeftIcon size={28} className={style.text} />
							<div className={style.text}>
								<strong>Gå tillbaka</strong>
							</div>
						</div>
						<FontAwesomeIcon
							icon={faRotate}
							cursor={"pointer"}
							spin={true}
							size={"xl"}
							style={{ margin: "auto 0" }}
						/>
					</div>

					<AbsolutCentered>
						<Loader />
					</AbsolutCentered>
				</div>
			)
		}

		return (
			<div className={style.tableWrapper}>
				<div style={{ display: "flex", gap: "15px", marginBottom: "10px" }}>
					<div
						className={style.cancelButton}
						onClick={() => {
							props.onCancel()
						}}>
						<ArrowLeftIcon size={28} className={style.text} />
						<div className={style.text}>
							<strong>Gå tillbaka</strong>
						</div>
					</div>
					<FontAwesomeIcon
						icon={faRotate}
						cursor={"pointer"}
						size={"xl"}
						style={{ margin: "auto 0" }}
						onClick={() => {
							fetchEvents()
						}}
					/>
				</div>
				{rowsPerPageSection(
					{},
					getPageSize(),
					(page) => {
						setPageAndFilterInUrl(1, page, null, setQueryParams)
					},
					false,
				)}
				<SkrappyTable data={events} columns={tableColumns()} />
				{tablePagingSection(
					paging,
					{ pageNumber: getPageNumber(), pageSize: getPageSize() },
					() => {
						setPageAndFilterInUrl(getPageNumber() - 1, null, null, setQueryParams)
					},
					() => {
						setPageAndFilterInUrl(getPageNumber() + 1, null, null, setQueryParams)
					},
					(number) => {
						setPageAndFilterInUrl(number, null, null, setQueryParams)
					},
					false,
				)}
			</div>
		)
	}

	if (!auth.IsLoggedInClient) {
		return null
	}

	return table()
}
